// =============================================================================
// SIREPPAR - Modal "Resolución" (reutilizable para todo acto resolutorio)
// =============================================================================
// Registra una resolución del Tribunal Electoral Provincial (TEP): aprueba/
// rechaza atributos, otorga/deniega el reconocimiento provisorio, aprueba/rechaza/
// aplaza un reempadronamiento. También sirve para la resolución con la que el
// SECRETARIO ELECTORAL aprueba un reempadronamiento por sí mismo: el formulario es
// el mismo y las etiquetas del órgano se pasan por config (labelNumero,
// prefijoNumero, labelFecha, labelMotivo, ayuda).
//
// En todos los casos OBLIGA a adjuntar al menos un archivo de la resolución
// (acta) + N° + fecha. El backend compone el formato "ACTA N°001/2026 (15/03/2026)".
//
// VARIOS ARCHIVOS y no solo un PDF: el TEP a veces resuelve en más de un instrumento
// (el acta y un anexo firmado aparte) y no siempre los manda escaneados en PDF —con
// frecuencia son fotos del acta firmada. Cada archivo queda como un Documento del
// expediente (sección "Resoluciones" y tab Informes, numerados (1), (2)…) y TODOS
// se le adjuntan al partido en el correo.
// =============================================================================
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import { api } from '../../services/api';

export interface ResolucionTepConfig {
  endpoint: string;        // p. ej. 'tep-aprueba-atributos'
  titulo: string;          // título del modal
  requiereMotivo: boolean; // true cuando el motivo es obligatorio
  tono: 'ok' | 'bad';      // color del botón (verde/rojo)
  successMsg: string;
  confirmMsg: string;      // confirm() antes de enviar
  confirmLabel: string;    // texto del botón de envío
  // Texto precargado (editable) del motivo. Si está definido, se muestra el campo
  // motivo aunque no sea obligatorio (p. ej. otorga/deniega provisorio).
  motivoInicial?: string;
  // Prefijo de la API. Por defecto 'reconocimiento'; el circuito de "no
  // presentado" (TEP rechaza / TEP aplaza) pasa 'no-presentado'.
  basePath?: string;
  // Check al pie para decidir si se le avisa al partido (se manda como
  // notificarPartido). Sin esto el modal no lo muestra y no lo manda.
  conAvisoPartido?: boolean;
  labelAvisoPartido?: string;
  // Tildado de arranque. Por defecto NO: la mayoría de estos actos se notifican
  // por cédula o publicación y el correo sería un duplicado. Las resoluciones que
  // resuelven el fondo del trámite lo pasan en true.
  avisoPartidoPorDefecto?: boolean;
  // ---- Etiquetas del órgano que resuelve ----------------------------------
  // El TEP resuelve por ACTA; el Secretario Electoral, por RESOLUCIÓN. Los
  // defaults son los del TEP, que es el circuito original: una config sin estos
  // campos se comporta exactamente como antes.
  labelNumero?: string;      // 'N° de acta'
  labelFecha?: string;       // 'Fecha del acta'
  prefijoNumero?: string;    // 'ACTA' → la ayuda dice "ACTA N°001/2026"
  labelMotivo?: string;      // 'Motivo'
  ayuda?: string;            // párrafo explicativo de arriba
}

interface Props {
  config: ResolucionTepConfig | null;
  expedienteId: string;
  onClose: () => void;
  onResuelto?: () => void;
}

/** Tope de archivos. Espeja MAX_ARCHIVOS_RESOLUCION del backend. */
const MAX_ARCHIVOS = 5;
const TIPOS_OK = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

export default function ModalResolucionTep({ config, expedienteId, onClose, onResuelto }: Props) {
  const [nroActa, setNroActa] = useState('');
  const [fecha, setFecha] = useState('');
  const [motivo, setMotivo] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [avisarPartido, setAvisarPartido] = useState(false);

  const reset = () => { setNroActa(''); setFecha(''); setMotivo(''); setArchivos([]); setAvisarPartido(false); };
  const cerrar = () => { reset(); onClose(); };

  // Al abrir con una config nueva, precargar el motivo (si trae texto inicial).
  useEffect(() => {
    if (config) {
      setMotivo(config.motivoInicial ?? '');
      setAvisarPartido(config.avisoPartidoPorDefecto === true);
    }
  }, [config]);

  const mostrarMotivo = !!config && (config.requiereMotivo || config.motivoInicial !== undefined);
  // Etiquetas del órgano que resuelve. Los defaults son los del TEP.
  const labelNumero = config?.labelNumero || 'N° de acta';
  const labelFecha = config?.labelFecha || 'Fecha del acta';
  const prefijoNumero = config?.prefijoNumero || 'ACTA';
  const labelMotivo = config?.labelMotivo || 'Motivo';

  // Alta de archivos: se acumulan (el input `multiple` puede usarse varias veces) y
  // se filtra por tipo acá para no llegar al backend con un .docx.
  const agregarArchivos = (nuevos: FileList | null) => {
    if (!nuevos || nuevos.length === 0) return;
    const validos: File[] = [];
    for (const f of Array.from(nuevos)) {
      if (!TIPOS_OK.includes(f.type) && !/\.(pdf|jpe?g|png|webp)$/i.test(f.name)) {
        toast.error(`"${f.name}" no es un PDF ni una imagen.`);
        continue;
      }
      validos.push(f);
    }
    setArchivos((prev) => {
      const total = [...prev, ...validos];
      if (total.length > MAX_ARCHIVOS) {
        toast.error(`Podés adjuntar hasta ${MAX_ARCHIVOS} archivos.`);
        return total.slice(0, MAX_ARCHIVOS);
      }
      return total;
    });
  };
  const quitarArchivo = (idx: number) => setArchivos((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!config) return;
    if (archivos.length === 0) { toast.error('Adjuntá al menos un archivo de la resolución'); return; }
    const nro = parseInt(nroActa, 10);
    if (!Number.isInteger(nro) || nro < 1) { toast.error(`Ingresá un ${labelNumero.toLowerCase()} válido`); return; }
    if (!fecha) { toast.error(`Ingresá la ${labelFecha.toLowerCase()}`); return; }
    if (config.requiereMotivo && !motivo.trim()) { toast.error(`Indicá el ${labelMotivo.toLowerCase()}`); return; }
    if (config.confirmMsg && !window.confirm(config.confirmMsg)) return;

    const [anio, mes, dia] = fecha.split('-').map((n) => parseInt(n, 10));
    const fd = new FormData();
    // Campo en plural: el backend lo lee con multer .array('archivos') en TODOS los
    // circuitos que usan este modal (reconocimiento y no presentado).
    archivos.forEach((f) => fd.append('archivos', f));
    fd.append('nroActa', String(nro));
    fd.append('dia', String(dia));
    fd.append('mes', String(mes));
    fd.append('anio', String(anio));
    if (motivo.trim()) fd.append('motivo', motivo.trim());
    if (config.conAvisoPartido) fd.append('notificarPartido', String(avisarPartido));

    setEnviando(true);
    try {
      await api.post(`/${config.basePath || 'reconocimiento'}/${expedienteId}/${config.endpoint}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(config.successMsg);
      onResuelto?.();
      cerrar();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al registrar la resolución');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal isOpen={config !== null} onClose={cerrar} title={config?.titulo ?? 'Resolución del TEP'} size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
          {config?.ayuda ??
            'Adjuntá la resolución del TEP. Quedan como ' +
            'respaldo documental del acto, en la sección "Resoluciones" del expediente, y se le ' +
            'envía al partido por correo.'}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{labelNumero} *</label>
            <input
              type="number"
              min={1}
              value={nroActa}
              onChange={(e) => setNroActa(e.target.value)}
              placeholder="1"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Se guarda como {prefijoNumero} N°{nroActa ? String(parseInt(nroActa, 10) || 0).padStart(3, '0') : '001'}/{fecha ? fecha.slice(0, 4) : 'AAAA'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{labelFecha} *</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Archivos de la resolución * <span className="font-normal text-gray-400">(PDF o imagen, hasta {MAX_ARCHIVOS})</span>
          </label>
          <input
            type="file"
            multiple
            accept="application/pdf,.pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => { agregarArchivos(e.target.files); e.target.value = ''; }}
            className="text-sm"
            disabled={archivos.length >= MAX_ARCHIVOS}
          />
          {archivos.length > 0 && (
            <ul className="mt-2 space-y-1">
              {archivos.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-xs bg-gray-50 border rounded px-2 py-1">
                  <span className="truncate">({i + 1}) {f.name}</span>
                  <button
                    type="button"
                    onClick={() => quitarArchivo(i)}
                    className="text-red-600 hover:text-red-700 shrink-0"
                    title="Quitar"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {mostrarMotivo && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{labelMotivo}{config?.requiereMotivo ? ' *' : ' (opcional)'}</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={4}
              placeholder="Fundamentos de la resolución…"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm"
            />
          </div>
        )}

        {config?.conAvisoPartido && (
          <label className="flex items-start gap-2 text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-lg p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4"
              checked={avisarPartido}
              onChange={(e) => setAvisarPartido(e.target.checked)}
            />
            <span>
              {config.labelAvisoPartido || 'Notificar la resolución al partido por correo'}
              <span className="block text-xs text-gray-500">
                Se le envían los archivos de la resolución junto con el texto cargado arriba.
              </span>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={cerrar} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={enviando}
            className={`px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 ${
              config?.tono === 'bad' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {enviando ? 'Registrando…' : (config?.confirmLabel ?? 'Registrar')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
